import React, { useEffect, useRef, useState, useCallback, useImperativeHandle } from 'react';
import * as THREE from 'three';

const MultiplayerCars = React.forwardRef(({ scene, groupId, currentPlayerId, playerInfo }, ref) => {
  const carsRef = useRef(new Map()); // Map of playerId -> car mesh
  const animationIdRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_URL;

  // Debug logging for props
  useEffect(() => {
    console.log('MultiplayerCars: Component mounted with props:', {
      groupId,
      currentPlayerId,
      hasScene: !!scene,
      playerInfo
    });
  }, [groupId, currentPlayerId, scene, playerInfo]);

  // Create a car mesh for another player
  const createPlayerCar = useCallback((color, laneNumber, playerName = 'Player') => {
    const carGroup = new THREE.Group();
    
    // Validate and provide default color if needed
    if (!color || typeof color !== 'string' || !color.startsWith('#')) {
      console.warn('MultiplayerCars: Invalid color provided:', color, 'using default color');
      color = '#FF6B6B'; // Default red color
    }
    
    // Car body
    const bodyGeometry = new THREE.BoxGeometry(2, 1, 4);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: color });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.5;
    carGroup.add(body);

    // Car top
    const topGeometry = new THREE.BoxGeometry(1.5, 0.8, 2.5);
    const topMaterial = new THREE.MeshStandardMaterial({ color: color });
    const top = new THREE.Mesh(topGeometry, topMaterial);
    top.position.y = 1.4;
    top.position.z = -0.3;
    carGroup.add(top);

    // Wheels
    const wheelGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.3, 8);
    const wheelMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    
    const frontLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    frontLeftWheel.rotation.z = Math.PI / 2;
    frontLeftWheel.position.set(-1.2, 0.4, 1.5);
    carGroup.add(frontLeftWheel);
    
    const frontRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    frontRightWheel.rotation.z = Math.PI / 2;
    frontRightWheel.position.set(1.2, 0.4, 1.5);
    carGroup.add(frontRightWheel);
    
    const backLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    backLeftWheel.rotation.z = Math.PI / 2;
    backLeftWheel.position.set(-1.2, 0.4, -1.5);
    carGroup.add(backLeftWheel);
    
    const backRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    backRightWheel.rotation.z = Math.PI / 2;
    backRightWheel.position.set(1.2, 0.4, -1.5);
    carGroup.add(backRightWheel);

    // Headlights
    const headlightGeometry = new THREE.SphereGeometry(0.2, 8, 8);
    const headlightMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xffffcc,
      emissive: 0xffffcc,
      emissiveIntensity: 0.3
    });
    
    const leftHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
    leftHeadlight.position.set(-0.5, 0.8, 2.2);
    carGroup.add(leftHeadlight);
    
    const rightHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
    rightHeadlight.position.set(0.5, 0.8, 2.2);
    carGroup.add(rightHeadlight);

    // Player name label
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 64;
    
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#000000';
    context.font = '24px Arial';
    context.textAlign = 'center';
    context.fillText(playerName, canvas.width / 2, canvas.height / 2 + 8);
    
    const texture = new THREE.CanvasTexture(canvas);
    const labelGeometry = new THREE.PlaneGeometry(3, 0.8);
    const labelMaterial = new THREE.MeshBasicMaterial({ 
      map: texture,
      transparent: true,
      side: THREE.DoubleSide
    });
    
    const label = new THREE.Mesh(labelGeometry, labelMaterial);
    label.position.y = 2.5;
    label.rotation.x = -Math.PI / 2;
    carGroup.add(label);

    return carGroup;
  }, []);

  // Update car position based on progress
  const updatePlayerCarPosition = useCallback((playerId, progress) => {
    if (!carsRef.current.has(playerId)) {
      console.log('MultiplayerCars: Car not found for player:', playerId);
      return;
    }

    const car = carsRef.current.get(playerId);
    const progressPercent = progress || 0;
    const carZ = (progressPercent / 100) * 1000; // Convert percentage to 3D position
    
    // Keep the same X and Y position, only update Z (progress)
    car.position.z = carZ;
    console.log(`MultiplayerCars: Updated car position for player ${playerId} to ${progressPercent}% (Z: ${carZ})`);
  }, []);

  // Create or update a player's car
  const updatePlayerCar = useCallback((player) => {
    if (!scene) {
      console.error('MultiplayerCars: Scene is not available');
      return;
    }

    // Validate required player data
    if (!player.device_id || !player.player_color || !player.lane_number) {
      console.error('MultiplayerCars: Invalid player data:', player);
      return;
    }

    const playerId = player.device_id;
    
    console.log('MultiplayerCars: Updating car for player:', playerId, 'with data:', player);
    
    // Check if car already exists - if so, just update its position
    if (carsRef.current.has(playerId)) {
      console.log('MultiplayerCars: Car already exists for player:', playerId, 'updating position only');
      const progressPercent = player.progress || 0;
      updatePlayerCarPosition(playerId, progressPercent);
      return;
    }

    // Create new car for this player
    const playerName = `Player ${player.lane_number}`;
    const carGroup = createPlayerCar(player.player_color, player.lane_number, playerName);
    
    // Position car based on player's progress
    // Convert progress percentage to 3D position (0-1000 range)
    const progressPercent = player.progress || 0;
    const carZ = (progressPercent / 100) * 1000; // Convert percentage to 3D position
    
    // Calculate lane position: Road is 30 units wide, divided into 5 lanes
    // Lane 1: -12.5 to -7.5, Lane 2: -7.5 to -2.5, Lane 3: -2.5 to 2.5, Lane 4: 2.5 to 7.5, Lane 5: 7.5 to 12.5
    const laneWidth = 5; // 30 / 6 = 5 units per lane
    const laneOffset = (player.lane_number - 3) * laneWidth; // Center lanes around 0
    
    console.log('MultiplayerCars: Creating new car at lane:', player.lane_number, 'offset:', laneOffset, 'progress:', progressPercent, 'z:', carZ);
    
    carGroup.position.set(laneOffset, 0.5, carZ);
    
    // Store reference and add to scene
    carsRef.current.set(playerId, carGroup);
    scene.add(carGroup);
    console.log('MultiplayerCars: Added new car to scene for player:', playerId);
  }, [scene, createPlayerCar, updatePlayerCarPosition]);

  // Fetch and update other players' positions
  const updateOtherPlayers = useCallback(async () => {
    if (!groupId || !currentPlayerId) return;

    try {
      console.log('MultiplayerCars: Fetching players for group:', groupId);
      const response = await fetch(`${API_BASE}/api/groups/${groupId}/players`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.players) {
        console.log('MultiplayerCars: Found players:', data.players);
        data.players.forEach(player => {
          // Skip current player
          if (player.device_id === currentPlayerId) {
            console.log('MultiplayerCars: Skipping current player:', player.device_id);
            return;
          }
          
          console.log('MultiplayerCars: Creating/updating car for player:', player);
          console.log('MultiplayerCars: Player color:', player.player_color, 'lane:', player.lane_number);
          // Create or update car for this player
          updatePlayerCar(player);
        });
      } else {
        console.log('MultiplayerCars: No players data or request failed:', data);
      }
    } catch (error) {
      console.error('MultiplayerCars: Failed to fetch other players:', error);
      // Don't spam the console with errors, just log once
    }
  }, [groupId, currentPlayerId, updatePlayerCar]);

  // Update all players' positions whenever there's a tap
  const updateAllPlayersPositions = useCallback(async () => {
    console.log('MultiplayerCars: updateAllPlayersPositions called!', { groupId, currentPlayerId });
    
    if (!groupId || !currentPlayerId) {
      console.log('MultiplayerCars: Missing groupId or currentPlayerId, aborting update');
      return;
    }

    try {
      console.log('MultiplayerCars: Fetching players for group:', groupId);
      const response = await fetch(`${API_BASE}/api/groups/${groupId}/players`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.players) {
        console.log('MultiplayerCars: Found players:', data.players);
        data.players.forEach(player => {
          // Skip current player
          if (player.device_id === currentPlayerId) {
            console.log('MultiplayerCars: Skipping current player:', player.device_id);
            return;
          }
          
          console.log('MultiplayerCars: Updating car for player:', player);
          
          // Create or update car for this player
          updatePlayerCar(player);
        });
      } else {
        console.log('MultiplayerCars: No players data or request failed:', data);
      }
    } catch (error) {
      console.error('MultiplayerCars: Failed to update all players:', error);
    }
  }, [groupId, currentPlayerId, updatePlayerCar]);

  // Clean up cars when component unmounts
  const cleanupCars = useCallback(() => {
    if (carsRef.current) {
      carsRef.current.forEach((car, playerId) => {
        if (scene) {
          scene.remove(car);
        }
      });
      carsRef.current.clear();
    }
  }, [scene]);



  // Animation loop to update other players' positions
  useEffect(() => {
    if (!groupId || !currentPlayerId) return;

    // Initial update to get all players
    updateOtherPlayers();

    // Set up polling for player discovery (new players joining)
    const interval = setInterval(updateOtherPlayers, 10000); // Update every 10 seconds (just for discovery)

    return () => {
      clearInterval(interval);
      cleanupCars();
    };
  }, [groupId, currentPlayerId, updateOtherPlayers, cleanupCars]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupCars();
    };
  }, [cleanupCars]);

  // Expose the update function to parent component
  useImperativeHandle(ref, () => ({
    updateAllPlayersPositions
  }), [updateAllPlayersPositions]);

  return null; // This component doesn't render anything visible
});

export default MultiplayerCars;
